import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { Loader2, RefreshCw, Play, Filter, Clock } from 'lucide-react';
import { operatorAPI } from '../../lib/api';

export default function OperatorExecutionLogs() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [typeSummary, setTypeSummary] = useState({});
  const [totalCount, setTotalCount] = useState(0);
  
  // Filters
  const [familyId, setFamilyId] = useState('');
  const [logType, setLogType] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [offset, setOffset] = useState(0);
  const limit = 50;
  
  // Scheduler status
  const [schedulerStatus, setSchedulerStatus] = useState(null);
  const [simulationResult, setSimulationResult] = useState(null);
  const [simulating, setSimulating] = useState(false);

  const logTypeColors = {
    pickup: 'bg-blue-100 text-blue-800',
    lunch: 'bg-orange-100 text-orange-800',
    activity_done: 'bg-green-100 text-green-800',
    activity_skip: 'bg-yellow-100 text-yellow-800',
    note: 'bg-purple-100 text-purple-800',
    scheduler_skipped: 'bg-gray-100 text-gray-800',
    holiday_reminder: 'bg-pink-100 text-pink-800',
  };
  
  // Severity color mapping based on log status/type
  const getSeverityColor = (log) => {
    const source = log.source || '';
    const valueJson = log.value_json || {};
    const hasError = valueJson.error || source.includes('error') || source.includes('failed');
    const hasSid = valueJson.twilio_sid || valueJson.twilio_sids?.length > 0;
    
    if (hasError) return 'bg-red-100 text-red-800 border-red-300'; // ERROR
    if (source.includes('skip') || source.includes('warn')) return 'bg-orange-100 text-orange-800 border-orange-300'; // WARNING
    if (hasSid) return 'bg-green-100 text-green-800 border-green-300'; // SUCCESS
    return 'bg-blue-100 text-blue-800 border-blue-300'; // INFO
  };
  
  const getSeverityLabel = (log) => {
    const source = log.source || '';
    const valueJson = log.value_json || {};
    const hasError = valueJson.error || source.includes('error') || source.includes('failed');
    const hasSid = valueJson.twilio_sid || valueJson.twilio_sids?.length > 0;
    
    if (hasError) return 'ERROR';
    if (source.includes('skip') || source.includes('warn')) return 'WARNING';
    if (hasSid) return 'SUCCESS';
    return 'INFO';
  };

  // Load data on mount
  useEffect(() => {
    let isMounted = true;
    
    const loadInitialData = async () => {
      setLoading(true);
      try {
        const [logsRes, statusRes] = await Promise.all([
          operatorAPI.getExecutionLogs({ limit, offset: 0 }),
          operatorAPI.getSchedulerStatus()
        ]);
        
        if (!isMounted) return;
        
        if (logsRes.data.ok) {
          setLogs(logsRes.data.logs || []);
          setTypeSummary(logsRes.data.type_summary || {});
          setTotalCount(logsRes.data.total_count || 0);
        }
        
        if (statusRes.data.ok) {
          setSchedulerStatus(statusRes.data);
        }
      } catch (err) {
        if (isMounted) {
          setError(err.response?.data?.detail || err.message || 'Error loading data');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };
    
    loadInitialData();
    
    return () => { isMounted = false; };
  }, []); // Only run on mount

  // Handle pagination
  useEffect(() => {
    if (offset === 0) return; // Skip initial mount
    
    let isMounted = true;
    
    const loadPage = async () => {
      setLoading(true);
      try {
        const response = await operatorAPI.getExecutionLogs({
          family_id: familyId || undefined,
          log_type: logType || undefined,
          start_date: startDate || undefined,
          end_date: endDate || undefined,
          limit,
          offset
        });
        
        if (!isMounted) return;
        
        if (response.data.ok) {
          setLogs(response.data.logs || []);
          setTypeSummary(response.data.type_summary || {});
          setTotalCount(response.data.total_count || 0);
        }
      } catch (err) {
        if (isMounted) {
          setError(err.response?.data?.detail || err.message);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };
    
    loadPage();
    
    return () => { isMounted = false; };
  }, [offset]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRefresh = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await operatorAPI.getExecutionLogs({
        family_id: familyId || undefined,
        log_type: logType || undefined,
        start_date: startDate || undefined,
        end_date: endDate || undefined,
        limit,
        offset
      });
      if (response.data.ok) {
        setLogs(response.data.logs || []);
        setTypeSummary(response.data.type_summary || {});
        setTotalCount(response.data.total_count || 0);
      }
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleApplyFilters = async () => {
    setOffset(0);
    setLoading(true);
    setError(null);
    try {
      const response = await operatorAPI.getExecutionLogs({
        family_id: familyId || undefined,
        log_type: logType || undefined,
        start_date: startDate || undefined,
        end_date: endDate || undefined,
        limit,
        offset: 0
      });
      if (response.data.ok) {
        setLogs(response.data.logs || []);
        setTypeSummary(response.data.type_summary || {});
        setTotalCount(response.data.total_count || 0);
      }
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  };

  const runSimulation = async () => {
    setSimulating(true);
    setSimulationResult(null);
    try {
      const response = await operatorAPI.runFullDaySimulation();
      setSimulationResult(response.data);
      await handleRefresh();
    } catch (err) {
      setSimulationResult({ ok: false, error: err.message });
    } finally {
      setSimulating(false);
    }
  };

  const clearFilters = () => {
    setFamilyId('');
    setLogType('');
    setStartDate('');
    setEndDate('');
    setOffset(0);
  };

  return (
    <div className="min-h-screen bg-slate-50 p-4 md:p-6" data-testid="execution-logs-page">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Execution Logs</h1>
            <p className="text-slate-600 text-sm">Debug center for scheduler and nanny actions</p>
          </div>
          <div className="flex gap-2">
            <Button 
              onClick={handleRefresh} 
              variant="outline" 
              size="sm"
              disabled={loading}
              data-testid="refresh-logs-btn"
            >
              <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button 
              onClick={runSimulation} 
              size="sm"
              disabled={simulating}
              className="bg-indigo-600 hover:bg-indigo-700"
              data-testid="run-simulation-btn"
            >
              {simulating ? (
                <Loader2 className="h-4 w-4 mr-1 animate-spin" />
              ) : (
                <Play className="h-4 w-4 mr-1" />
              )}
              Run Full Day Simulation
            </Button>
          </div>
        </div>

        {/* Scheduler Status */}
        {schedulerStatus && (
          <Card data-testid="scheduler-status-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <Clock className="h-5 w-5" />
                Scheduler Status
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <span className="text-slate-500">IST Time:</span>
                  <p className="font-medium">{schedulerStatus.current_ist_time}</p>
                </div>
                <div>
                  <span className="text-slate-500">Timezone:</span>
                  <p className="font-medium">{schedulerStatus.scheduler_timezone}</p>
                </div>
                <div>
                  <span className="text-slate-500">Status:</span>
                  <Badge variant={schedulerStatus.scheduler_running ? 'default' : 'destructive'}>
                    {schedulerStatus.scheduler_running ? 'Running' : 'Stopped'}
                  </Badge>
                </div>
                <div>
                  <span className="text-slate-500">Jobs:</span>
                  <p className="font-medium">{schedulerStatus.total_jobs} registered</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Simulation Result */}
        {simulationResult && (
          <Card className={simulationResult.ok ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'}>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">
                Simulation Result {simulationResult.ok ? '✅' : '❌'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {simulationResult.jobs?.map((job, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm">
                    <Badge variant={job.status === 'executed' ? 'default' : 'destructive'}>
                      {job.status}
                    </Badge>
                    <span>{job.job_name}</span>
                  </div>
                ))}
              </div>
              {simulationResult.error && (
                <p className="text-red-600 mt-2">{simulationResult.error}</p>
              )}
            </CardContent>
          </Card>
        )}

        {/* Filters */}
        <Card data-testid="filters-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Filter className="h-5 w-5" />
              Filters
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              <Input
                placeholder="Family ID"
                value={familyId}
                onChange={(e) => setFamilyId(e.target.value)}
                data-testid="filter-family-id"
              />
              <Select value={logType || "all"} onValueChange={(v) => setLogType(v === "all" ? "" : v)}>
                <SelectTrigger data-testid="filter-log-type">
                  <SelectValue placeholder="Log Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="pickup">Pickup</SelectItem>
                  <SelectItem value="lunch">Lunch</SelectItem>
                  <SelectItem value="activity_done">Activity Done</SelectItem>
                  <SelectItem value="activity_skip">Activity Skip</SelectItem>
                  <SelectItem value="note">Note</SelectItem>
                  <SelectItem value="scheduler_skipped">Scheduler Skipped</SelectItem>
                  <SelectItem value="holiday_reminder">Holiday Reminder</SelectItem>
                </SelectContent>
              </Select>
              <Input
                type="date"
                placeholder="Start Date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                data-testid="filter-start-date"
              />
              <Input
                type="date"
                placeholder="End Date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                data-testid="filter-end-date"
              />
              <div className="flex gap-2">
                <Button onClick={handleApplyFilters} size="sm" data-testid="apply-filters-btn">
                  Apply
                </Button>
                <Button onClick={clearFilters} variant="outline" size="sm" data-testid="clear-filters-btn">
                  Clear
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Type Summary */}
        {Object.keys(typeSummary).length > 0 && (
          <div className="flex flex-wrap gap-2" data-testid="type-summary">
            {Object.entries(typeSummary).map(([type, count]) => (
              <Badge key={type} className={logTypeColors[type] || 'bg-slate-100 text-slate-800'}>
                {type}: {count}
              </Badge>
            ))}
            <span className="text-sm text-slate-500 ml-2">Total: {totalCount}</span>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded" data-testid="error-message">
            {error}
          </div>
        )}

        {/* Logs Table */}
        <Card data-testid="logs-table-card">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-100">
                  <tr>
                    <th className="px-4 py-3 text-left font-medium text-slate-700">Time</th>
                    <th className="px-4 py-3 text-left font-medium text-slate-700">Severity</th>
                    <th className="px-4 py-3 text-left font-medium text-slate-700">Type</th>
                    <th className="px-4 py-3 text-left font-medium text-slate-700">Source</th>
                    <th className="px-4 py-3 text-left font-medium text-slate-700">Details</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={5} className="px-4 py-8 text-center">
                        <Loader2 className="h-6 w-6 animate-spin mx-auto text-slate-400" />
                      </td>
                    </tr>
                  ) : logs.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-4 py-8 text-center text-slate-500">
                        No logs found. Try adjusting filters or run a simulation.
                      </td>
                    </tr>
                  ) : (
                    logs.map((log) => (
                      <tr key={log.id} className={`border-b hover:bg-slate-50 ${getSeverityColor(log).includes('red') ? 'bg-red-50/50' : ''}`}>
                        <td className="px-4 py-3 text-slate-600">
                          {new Date(log.created_at).toLocaleString('en-IN', { 
                            timeZone: 'Asia/Kolkata',
                            dateStyle: 'short',
                            timeStyle: 'short'
                          })}
                        </td>
                        <td className="px-4 py-3">
                          <Badge className={getSeverityColor(log)}>
                            {getSeverityLabel(log)}
                          </Badge>
                        </td>
                        <td className="px-4 py-3">
                          <Badge className={logTypeColors[log.log_type] || 'bg-slate-100'}>
                            {log.log_type}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 text-slate-600">{log.source}</td>
                        <td className="px-4 py-3">
                          <code className="text-xs bg-slate-100 px-2 py-1 rounded">
                            {JSON.stringify(log.value_json).slice(0, 80)}
                            {JSON.stringify(log.value_json).length > 80 ? '...' : ''}
                          </code>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Pagination */}
        <div className="flex justify-between items-center" data-testid="pagination">
          <span className="text-sm text-slate-600">
            Showing {logs.length > 0 ? offset + 1 : 0}-{Math.min(offset + limit, totalCount)} of {totalCount}
          </span>
          <div className="flex gap-2">
            <Button 
              onClick={() => setOffset(Math.max(0, offset - limit))}
              disabled={offset === 0}
              variant="outline"
              size="sm"
              data-testid="prev-page-btn"
            >
              Previous
            </Button>
            <Button 
              onClick={() => setOffset(offset + limit)}
              disabled={offset + limit >= totalCount}
              variant="outline"
              size="sm"
              data-testid="next-page-btn"
            >
              Next
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
